import scheduledActionsAPI from '../../api/scheduledActions';

/**
 * Agendamentos pendentes — a lista do que a plataforma vai fazer sozinha.
 *
 * `records: null` é "não sei" (nunca busquei, ou o GET falhou); `[]` é "busquei
 * e não há nada agendado". A tela precisa da diferença: erro renderizado como
 * lista vazia diria ao operador que nada vai acontecer justamente quando ele
 * não faz ideia do que vai acontecer.
 *
 * `meta.truncated` vem do backend (teto de 300) e é divulgado na tela — lista
 * cortada em silêncio é o mesmo problema por outro caminho.
 */
export const state = {
  records: null,
  meta: { total: 0, truncated: false },
  // Filtro corrente. Guardado aqui pra a resposta atrasada de um filtro antigo
  // não sobrescrever a lista do filtro que o operador já escolheu.
  status: 'pending',
  // Período corrente (epoch s) — o calendário busca só a semana/mês visível.
  // `null` = sem recorte (a lista).
  range: null,
  error: null,
  uiFlags: {
    isFetching: false,
    // Id do agendamento em cancelamento (um por vez) — o botão da linha usa
    // isso pra ficar em loading e desabilitado, o que mata o duplo POST.
    cancelingId: null,
  },
};

export const getters = {
  records: _state => _state.records,
  isLoaded: _state => Array.isArray(_state.records),
  meta: _state => _state.meta,
  status: _state => _state.status,
  range: _state => _state.range,
  error: _state => _state.error,
  uiFlags: _state => _state.uiFlags,
  isCanceling: _state => id => _state.uiFlags.cancelingId === id,
};

/**
 * O erro do backend é o texto que EXPLICA (ex.: "agendamento não está mais
 * pendente" no 422 do cancel). Quem chama mostra ele, não um "falhou" genérico.
 */
export const scheduledActionErrorMessage = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  null;

// Releitura COALESCIDA por evento de tempo real. O evento é só "algo mudou"
// (kind/status), de propósito: os itens prontos vêm da Query, e uma rajada
// (sweeper disparando 200 timers/min) vira UMA releitura por janela em vez de
// 200. Sem botão "Atualizar": a tela é fiel ao banco enquanto o canal está
// vivo, e a reconexão do ActionCable já refaz o estado.
const REALTIME_REFRESH_DEBOUNCE_MS = 400;

const sameRange = (a, b) =>
  (a?.from || null) === (b?.from || null) &&
  (a?.to || null) === (b?.to || null);
const isCurrentQuery = (s, status, range) =>
  status === s.status && sameRange(range, s.range);
let realtimeTimer = null;

export const actions = {
  onRealtimeChange({ state: s, dispatch }) {
    // Tela nunca aberta (ou fechada com erro): nada a manter fresco.
    if (!Array.isArray(s.records)) return;
    clearTimeout(realtimeTimer);
    realtimeTimer = setTimeout(() => {
      realtimeTimer = null;
      dispatch('fetch', { status: s.status, ...(s.range || {}) }).catch(() => {
        // O erro já foi pro state e a tela mostra "tentar de novo".
      });
    }, REALTIME_REFRESH_DEBOUNCE_MS);
  },

  async fetch({ commit }, { status = 'pending', from = null, to = null } = {}) {
    const range = from || to ? { from, to } : null;
    commit('SET_QUERY', { status, range });
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await scheduledActionsAPI.list({ status, from, to });
      commit('SET_RECORDS', {
        status,
        range,
        records: data.payload || [],
        meta: data.meta || { total: 0, truncated: false },
      });
      return data.payload || [];
    } catch (error) {
      commit('SET_ERROR', {
        status,
        range,
        message: scheduledActionErrorMessage(error),
      });
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  /**
   * Cancela UM agendamento. O 422 (Cancel::NOT_FOUND) não é ruído: ele
   * significa que a tela está velha, então propagamos o erro pra quem chama
   * mostrar o texto do backend e re-buscar a lista.
   */
  async cancel({ commit }, { id }) {
    commit('SET_UI_FLAG', { cancelingId: id });
    try {
      await scheduledActionsAPI.cancel(id);
      commit('REMOVE_RECORD', id);
    } finally {
      commit('SET_UI_FLAG', { cancelingId: null });
    }
  },
};

export const mutations = {
  SET_UI_FLAG(_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  SET_QUERY(_state, { status, range }) {
    _state.status = status;
    _state.range = range;
  },
  SET_RECORDS(_state, { status, range, records, meta }) {
    // Resposta de um filtro/período que não é mais o corrente é descartada.
    if (!isCurrentQuery(_state, status, range)) return;
    _state.records = [...records];
    _state.meta = { ...meta };
    _state.error = null;
  },
  SET_ERROR(_state, { status, range, message }) {
    if (!isCurrentQuery(_state, status, range)) return;
    // Some com a lista de propósito: mostrar dado velho sob um erro novo faria
    // a tela afirmar coisas que ela não sabe mais.
    _state.records = null;
    _state.meta = { total: 0, truncated: false };
    _state.error = message;
  },
  REMOVE_RECORD(_state, id) {
    if (!Array.isArray(_state.records)) return;
    const next = _state.records.filter(record => record.id !== id);
    _state.records = next;
    _state.meta = {
      ..._state.meta,
      total: Math.max(0, (_state.meta.total || 0) - 1),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
